
import { motion } from "framer-motion";
import { Film, Share2, User, Target, Shield, Star, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";

const services = [
  {
    title: "Production",
    description: "Bringing cinematic visions to life from concept to completion with technical precision and creative soul.",
    Icon: Film,
    link: "/production"
  },
  {
    title: "Distribution and Marketing", 
    description: "Ensuring your content reaches its audience through theatres, TV, and global streaming platforms.",
    Icon: Share2,
    link: "/distribution-marketing"
  },
  {
    title: "Celebrity Management",
    description: "Trusted name in celebrity and influencer management, collaborating with top talent from film, television, and digital media.",
    Icon: User,
    link: "/celebrity-management"
  },
  {
    title: "Media Strategy",
    description: "Building emotional bridges between content and consumers with data-informed, audience-tuned strategies.",
    Icon: Target,
    link: "/media-strategy"
  },
  {
    title: "Anti-Piracy Solutions",
    description: "Protecting your intellectual property with advanced anti-piracy solutions and content monitoring.",
    Icon: Shield,
    link: "/anti-piracy"
  },
  {
    title: "Online Reputation Management (ORM)",
    description: "Managing and enhancing your digital presence and reputation across all online platforms.",
    Icon: Star,
    link: "/orm"
  }
];

export const Services = () => {
  const { colors } = useTheme();
  
  return (
    <section 
      id="services" 
      className="py-16 md:py-20 px-4 relative overflow-hidden transition-colors duration-300"
      style={{ backgroundColor: colors.background, color: colors.foreground }}
    >
      {/* Minimal background - no animations */}
      <div className="absolute inset-0 -z-10 opacity-30">
        <div 
          className="absolute top-20 left-20 w-40 md:w-60 h-40 md:h-60 rounded-full blur-3xl opacity-20"
          style={{ backgroundColor: `${colors.primary}10` }}
        />
        <div 
          className="absolute bottom-20 right-20 w-40 md:w-60 h-40 md:h-60 rounded-full blur-3xl opacity-15"
          style={{ backgroundColor: `${colors.primary}08` }}
        />
      </div>

      <div className="max-w-6xl mx-auto">
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.3 }}
          className="text-4xl md:text-5xl font-bold text-center mb-12 md:mb-16"
          style={{ color: colors.foreground }}
        >
          Our{" "}
          <span 
            className="bg-clip-text text-transparent"
            style={{ background: colors.gradient, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
          >
            Services
          </span>
          <motion.div 
            className="w-24 h-1 mx-auto mb-8 mt-6"
            style={{ backgroundColor: colors.primary }}
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
          />
        </motion.h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="group relative"
            >
              <Link 
                to={service.link}
                className="relative flex flex-col p-5 md:p-6 rounded-2xl border-2 backdrop-blur-sm
                          hover:border-opacity-90 transition-all duration-300 h-full
                          transform hover:-translate-y-2 hover:scale-[1.02]"
                style={{ 
                  backgroundColor: `${colors.card}90`,
                  borderColor: `${colors.primary}60`,
                  color: colors.foreground,
                  boxShadow: `0 8px 25px ${colors.primary}15`
                }}
                onClick={() => {
                  setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }, 100);
                }}
              >
                {/* Simplified icon container */}
                <motion.div
                  className="relative z-10 mb-4 w-14 h-14 md:w-16 md:h-16 mx-auto"
                  whileHover={{ 
                    scale: 1.1,
                    transition: { duration: 0.2 }
                  }}
                >
                  <div
                    className="w-full h-full rounded-2xl flex items-center justify-center backdrop-blur-sm"
                    style={{
                      background: `linear-gradient(135deg, ${colors.primary}20, ${colors.secondary}15)`,
                      border: `2px solid ${colors.primary}50`,
                      boxShadow: `0 4px 12px ${colors.primary}20`
                    }}
                  >
                    <service.Icon 
                      className="w-8 h-8 md:w-9 md:h-9 transition-colors duration-200"
                      style={{ color: colors.primary }}
                    />
                  </div>
                </motion.div>
                
                <h3 
                  className="relative z-10 text-xl md:text-2xl font-bold mb-3 text-center"
                  style={{ color: colors.primary }}
                >
                  {service.title}
                </h3>
                
                <p 
                  className="relative z-10 text-sm md:text-base text-center mb-auto leading-relaxed flex-grow"
                  style={{ color: colors.secondary }}
                >
                  {service.description}
                </p>
                
                <motion.div 
                  className="relative z-10 flex items-center justify-center text-sm font-bold mt-4"
                  style={{ color: colors.primary }}
                  whileHover={{ 
                    x: 4,
                    transition: { duration: 0.2 }
                  }}
                >
                  <span className="mr-2">Explore Service</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                </motion.div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
